import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { useAuth } from "../context/AuthContext";
import { FileText, Download, ScrollText, ReceiptText, FileSignature } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  purchase_order: { label: "Purchase Orders", icon: ScrollText },
  contract: { label: "Contracts", icon: FileSignature },
  bill: { label: "Bills", icon: ReceiptText },
  document: { label: "Documents", icon: FileText },
};

const Dashboard = () => {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/documents/me`).then((r) => setDocs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const grouped = docs.reduce((acc, d) => {
    (acc[d.type] = acc[d.type] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="bg-slate-900 text-white pt-36 pb-16 relative">
        <div className="container mx-auto px-4">
          <span className="text-gold font-bold uppercase tracking-widest text-sm mb-2 block">My Account</span>
          <h1 className="text-4xl font-display font-bold uppercase tracking-wide">Welcome, {user?.name}</h1>
          <p className="text-gray-300 mt-2">Your purchase orders, contracts, bills and documents.</p>
        </div>
      </div>

      <main className="flex-grow py-16 bg-gray-50" data-testid="user-dashboard">
        <div className="container mx-auto px-4 space-y-12">
          {loading ? (
            <p className="text-center text-gray-500">Loading your documents…</p>
          ) : docs.length === 0 ? (
            <div className="text-center py-16 bg-white border border-gray-100 shadow-sm">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No documents have been shared with you yet.</p>
              <p className="text-gray-400 text-sm mt-1">Your administrator will upload purchase orders, contracts and bills here.</p>
            </div>
          ) : (
            Object.keys(TYPE_META).map((type) => {
              const items = grouped[type] || [];
              if (items.length === 0) return null;
              const Meta = TYPE_META[type];
              const Icon = Meta.icon;
              return (
                <section key={type} data-testid={`doc-group-${type}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2.5 bg-primary text-white rounded-sm"><Icon size={22} /></div>
                    <h2 className="text-2xl font-display font-bold text-foreground">{Meta.label}</h2>
                    <span className="text-sm text-gray-400">({items.length})</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {items.map((d) => (
                      <div key={d.id} className="bg-white border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid="doc-card">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <h3 className="font-display font-bold text-foreground">{d.title}</h3>
                          {d.amount && <span className="text-gold font-bold text-sm whitespace-nowrap">{d.amount}</span>}
                        </div>
                        {d.description && <p className="text-sm text-gray-500 mb-4">{d.description}</p>}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{new Date(d.created_at).toLocaleDateString()}</span>
                          {d.file_path ? (
                            <a href={`${BACKEND_URL}/api/${d.file_path}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm text-primary font-bold hover:underline" data-testid="doc-download">
                              <Download size={15} /> View
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No file attached</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
